import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../MainPages.module.css';
import SearchBar from '../../Components/UILibrary/SearchBar';
import CardPlaceholder from '../../Components/UILibrary/CardPlaceholder';
import DepartmentCard from '../../Components/Departments/DepartmentCard';
import { DepartmentsService } from '../../../Logic/DepartmentsService';
import type { IDepartmentView } from '@model/Department.model';
import { UserAuthenticationStoreImplementation } from 'Data/UserAuthentication/UserAuthenticationStoreImplementation';
import ConfirmModal from '../../Components/Modals/ConfirmModal';
import CreateEditDepartmentModal from '../../Components/Modals/CreateEditDepartmentModal';
import { useForceLogoutAndRedirect } from '../../../Logic/Functions/Hooks/useForceLogoutAndRedirect';
import { UnauthorizedPopupStoreImplementation } from 'Data/UnauthorizedPopup/UnauthorizedPopupStoreImplementation';

const ManageDepartments: React.FC = () => {
  const { showPopup } = UnauthorizedPopupStoreImplementation();
  const departmentService = new DepartmentsService();
  const authStore = UserAuthenticationStoreImplementation();
  const navigate = useNavigate();

  const onAuthError = useForceLogoutAndRedirect();

  const [allDepartments, setAllDepartments] = useState<IDepartmentView[]>([]);
  const [departments, setDepartments] = useState<IDepartmentView[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [subtitle, setSubtitle] = useState<string>('Total: 0');
  const [emptyMessage, setEmptyMessage] = useState<string>('No departments found.');
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteName, setDeleteName] = useState<string>('');
  const [editingDepartment, setEditingDepartment] = useState<IDepartmentView | null>(null);

  useEffect(() => {
    departmentService.handleGetAllDepartments({
      q: undefined,
      setLoading: setLoadingList,
      setApiError: setListError,
      token: authStore.authenticationState?.token ?? '',
      onAuthError,
      onForbidden: (msg) => {
        showPopup(msg);
        navigate('/', { replace: true });
      },
      setItems: (items) => {
        setAllDepartments(items);
      },
      setTotal: () => {},
    });
  }, [authStore.authenticationState?.token]);

  useEffect(() => {
    const view = departmentService.searchDepartmentsLocal({
      allDepartments,
      search,
    });
    setDepartments(view.items);
    setSubtitle(view.subtitle);
    setEmptyMessage(view.emptyMessage);
  }, [allDepartments, search]);

  const openDepartment = (id: string) => {
    navigate(`/departments/${id}`);
  };

  const editDepartment = (id: string) => {
    const dept = departmentService.findById(allDepartments, id);
    setEditingDepartment(dept);
    setCreateOpen(true);
  };

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteId) return;

    await departmentService.handleDeleteDepartment({
      id: deleteId,
      token: authStore.authenticationState?.token ?? '',
      setLoading: setLoadingList,
      setApiError: setListError,
      onAuthError,
      onForbidden: (msg) => {
        showPopup(msg);
        navigate('/', { replace: true });
      },
      onSuccess: () => {
        setAllDepartments((prev) => prev.filter((d) => d.id !== deleteId));
        setDeleteId(null);
        setDeleteName('');
      },
    });
  }, [deleteId, authStore.authenticationState?.token]);

  const handleOpenCreate = () => {
    setEditingDepartment(null);
    setCreateOpen(true);
  };

  const handleCloseCreateEdit = () => {
    setCreateOpen(false);
    setEditingDepartment(null);
  };

  const handleModalSuccess = useCallback(
    (updated: IDepartmentView) => {
      setAllDepartments((prev) => departmentService.addOrUpdateDepartmentInList(prev, updated));
    },
    [departmentService]
  );

  return (
    <main className={styles.Page} data-testid="md-page">
      <header className={styles.Header}>
        <h1 className={styles.Title}>Department Management</h1>

        <div className={styles.HeaderRight}>
          <div data-testid="md-searchbar">
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder="Search departments…"
              enableDropdown={false}
            />
          </div>

          <button
            type="button"
            className={styles.AddBtn}
            onClick={handleOpenCreate}
            data-testid="md-add"
          >
            Add Department
          </button>
        </div>
      </header>

      <p className={styles.mcSubtitlee} data-testid="md-subtitle">
        {subtitle}
      </p>

      {loadingList && (
        <div data-testid="md-loading">
          <CardPlaceholder count={6} customWrapperStyle={styles.mcList} />
        </div>
      )}

      {!loadingList && listError && (
        <div className={styles.Error} role="alert" data-testid="md-error">
          {listError}
        </div>
      )}

      {!loadingList && !listError && departments.length === 0 && (
        <div className={styles.Empty} data-testid="md-empty">
          {emptyMessage}
        </div>
      )}

      {!loadingList && !listError && departments.length > 0 && (
        <section className={styles.mcList} data-testid="md-list">
          {departments.map((department) => (
            <DepartmentCard
              key={department.id}
              department={department}
              onOpen={openDepartment}
              onEdit={editDepartment}
              onDelete={(id: string) => {
                setDeleteId(id);
                setDeleteName(department.name);
                setConfirmOpen(true);
              }}
            />
          ))}
        </section>
      )}

      <CreateEditDepartmentModal
        open={createOpen}
        onClose={handleCloseCreateEdit}
        forEdit={!!editingDepartment}
        department={editingDepartment}
        onSuccess={handleModalSuccess}
      />

      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={handleConfirmDelete}
        message={
          deleteName
            ? `Are you sure you want to delete the department "${deleteName}"?`
            : 'Are you sure you want to delete this department?'
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </main>
  );
};

export default ManageDepartments;
